import { useCallback, useContext, useMemo } from "react";
import { UNSAFE_FrameworkContext, useLocation } from "react-router";
import { getLocalizedPaths } from "~/lib/site-pages";
import { useLocalizationPreferences } from "~/lib/use-localization-preferences";
import { getCart, useSyncedCartField } from "~/lib/use-embedded-cart-global";

// Single source of truth for every supported locale — id, endonym display name/region
// (for the language picker), and which route it resolves to (en-US/en-CA share root;
// es-MX/es-US share /es). Add or remove a locale here only: SupportedLocale,
// SUPPORTED_LOCALES, LOCALE_PATHS, and SEGMENT_PATH below are all derived from this list.
export const LANGUAGE_ITEMS = [
  { id: "en-US", title: "English", region: "United States", path: "/" },
  { id: "en-CA", title: "English", region: "Canada", path: "/" },
  { id: "fr-CA", title: "Français", region: "Canada", path: "/fr-CA" },
  { id: "es-MX", title: "Español", region: "México", path: "/es" },
  { id: "es-US", title: "Español", region: "United States", path: "/es" },
] as const satisfies ReadonlyArray<{ id: string; title: string; region: string; path: string }>;

export type SupportedLocale = (typeof LANGUAGE_ITEMS)[number]["id"];

export const SUPPORTED_LOCALES: readonly SupportedLocale[] = LANGUAGE_ITEMS.map((item) => item.id);

// Route each locale resolves to.
export const LOCALE_PATHS: Record<SupportedLocale, string> = Object.fromEntries(
  LANGUAGE_ITEMS.map((item) => [item.id, item.path]),
) as Record<SupportedLocale, string>;

// A URL's first path segment (e.g. "fr-CA" in /fr-CA/blog) mapped back to the canonical
// locale path it belongs to. Derived from LOCALE_PATHS so a new locale's segment is
// picked up automatically as long as its `path` above is set correctly.
export const SEGMENT_PATH: Record<string, string> = Object.fromEntries(
  [...new Set(Object.values(LOCALE_PATHS))].filter((path) => path !== "/").map((path) => [path.slice(1), path]),
);

// Paths that exist under the /:locale layout, read from the route tree React Router
// ships to the client (react-router.config.ts sets routeDiscovery to "initial" so the
// manifest is complete). Nothing to maintain here: a page is localized exactly when
// routes.ts registers it under `:locale`. Without a router context (isolated component
// tests) nothing is localized.
export function useLocalizedPaths(): ReadonlySet<string> {
  const context = useContext(UNSAFE_FrameworkContext);
  const routes = context?.manifest.routes;
  return useMemo(() => (routes ? getLocalizedPaths(routes) : new Set<string>()), [routes]);
}

// Swaps pathname's locale segment (if any) for targetPath, preserving the rest of the
// path when a localized route for it exists — e.g. /products/drive -> /fr-CA -> targetPath
// gives /fr-CA/products/drive, not just /fr-CA.
export function localizedPath(pathname: string, targetPath: string, localized: ReadonlySet<string>): string {
  const [, firstSegment, ...rest] = pathname.split("/");
  const isLocalized = firstSegment in SEGMENT_PATH;
  const restPath = (isLocalized ? rest : [firstSegment, ...rest]).filter(Boolean).join("/");
  if (!restPath || !localized.has(`/${restPath}`)) return targetPath;
  return targetPath === "/" ? `/${restPath}` : `${targetPath}/${restPath}`;
}

// For internal nav links (header, footer, hero CTAs, etc.) whose href is a canonical
// English path like "/products/drive" — every such link was previously hardcoded, so
// clicking one from a /fr-CA or /es page silently dropped the user back into English.
// Prefixes `path` with the CURRENT page's locale (derived from the URL, not the stored
// preference) when a localized route for it exists; otherwise returns it unchanged.
export function useLocalizedPath(path: string): string {
  const { pathname } = useLocation();
  const localized = useLocalizedPaths();
  const [, firstSegment] = pathname.split("/");
  const currentLocalePath = SEGMENT_PATH[firstSegment] ?? "/";
  if (currentLocalePath === "/") return path;
  if (!localized.has(path === "" ? "/" : path)) return path;
  return `${currentLocalePath}${path}`;
}

function normalizeLanguage(value: string | undefined): SupportedLocale | undefined {
  return SUPPORTED_LOCALES.find((locale) => locale.toLowerCase() === value?.toLowerCase());
}

function getCartLanguage(): SupportedLocale | undefined {
  return normalizeLanguage(getCart()?.state?.getPersist().language);
}

// Mirrors useCurrencyPreference — a single access point, kept in sync via
// useSyncedCartField. checkoutdwa has no language picker yet, so `language` today only
// ever mirrors currency (en-us/en-ca); this exists so the preference is already wired
// for whenever checkoutdwa gains one, and can be exercised now by editing the cookie's
// language value directly and navigating back here.
export function useLocalePreference(): [SupportedLocale, (locale: SupportedLocale) => void] {
  const [{ locale }, setPreferences] = useLocalizationPreferences();
  const setLocale = useCallback((next: SupportedLocale) => setPreferences({ locale: next }), [setPreferences]);
  useSyncedCartField([locale, setLocale], getCartLanguage);
  return [locale, setLocale];
}

// en-US/en-CA (and es-MX/es-US) share the same route, so the URL alone can't tell them
// apart — fall back to the persisted locale preference when it agrees with the route's
// language, otherwise default to the first item for that language.
export function selectedLanguageId(pathname: string, locale: SupportedLocale): SupportedLocale {
  const [, firstSegment] = pathname.split("/");
  const currentPath = SEGMENT_PATH[firstSegment] ?? "/";
  const candidates = LANGUAGE_ITEMS.filter((item) => item.path === currentPath);
  const match = candidates.find((item) => item.id === locale);
  return (match ?? candidates[0]).id;
}
